import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppBar from '../components/AppBar'
import LoadingSpinner from '../components/LoadingSpinner'
import SubmitButton from '../components/SubmitButton'
import SheetHeader from '../components/SheetHeader'
import { kakaoApi, KakaoPlace } from '../api/kakao'
import { mapApi } from '../api/map'
import { MapFilterType } from '../constants/filter'
import { MapMarker } from '../types/mapMarker'
import { MAX_PHOTOS } from '../constants/layout'
import { useReviewStore } from '../stores/reviewStore'
import { PhotoItem } from '../types/photo'
import { dataURLtoFile } from '../utils/fileUtils'
import { isApp } from '../utils/platform'
import {
  BRIDGE_POLL_MS,
  BRIDGE_WAIT_MS,
  GRACE_MS,
  bridge,
  hasSharedPhotos,
  injectionDone,
} from '../utils/sharedPhotosBridge'
import { dialogUtil } from '../utils/dialog'
import { toastUtil } from '../utils/toast'

/**
 * 먹은 사진 담아두기 — 공유로 넘긴 사진을 기록으로 만드는 화면.
 *
 *   앱: 사진 공유 → SharedPhotosStore 에 보관 → /collect?shared=3
 *   웹: 여기서 고르고, 가게를 정하고, 기존 기록 화면으로 넘긴다
 *
 * 왜 웹이 하나
 * -----------
 * 사진을 받아 보관하는 데까지가 앱이고, 그 뒤는 전부 여기다. 고르는 방식도
 * 가게를 좁히는 방식도 써 보면서 계속 바뀔 텐데, 앱에 박으면 한 번 고칠 때마다
 * 스토어 심사다. 웹이면 배포 즉시 전원에게 간다.
 *
 * 묶지 않는다
 * ----------
 * 시각·좌표로 끼니를 자동으로 묶는 것도 해 봤지만(실측에서 25초·30m 차이 두 장이
 * 정확히 한 끼로 잡혔다) 넣지 않았다. 묶기가 틀리면 사용자가 **풀어내야** 하고,
 * 그 품이 직접 고르는 것보다 크다. 여러 장 고르면 기록 하나가 된다.
 *
 * 기록 화면으로 넘기는 법
 * ----------------------
 * StoreReview 는 "스토어에 담긴 storeId 와 주소의 storeId 가 다르면" 전역 상태를
 * 지운다. 그래서 넘기기 **전에** storeId 를 같이 심어 둔다. 안 그러면 사진이
 * 그 자리에서 날아간다.
 */

/** 한 번에 받아오는 사진 수. 앱의 _maxPage 와 맞춘다. */
const PAGE = 25

/**
 * 한 번에 담을 수 있는 최대 장수.
 *
 * 앱의 SharedPhotosStore.maxPerShare, 안드로이드 MainActivity.MAX_PICK,
 * iOS 확장 Info.plist 의 NSExtensionActivationSupportsImageWithMaxCount 와
 * **같은 값이어야 한다.** 여기서는 안내 문구에만 쓴다.
 */
const MAX_PER_SHARE = 100

/**
 * 사진 좌표 둘레로 가게를 찾을 반경(m).
 *
 * NearbyPhotoSuggestions 와 같은 80m 로 맞췄다. 그쪽은 50m 로 좁혔다가 "같은
 * 자리에서 찍은 사진인데도 일부만 잡힌다"는 실측 끝에 80m 로 넓힌 값이다 —
 * 실내 GPS 가 수십 m 씩 흩어진다는 뜻이고, 그 사정은 여기서도 같다.
 * 두 곳이 다른 값을 쓰면 한쪽을 고칠 때 다른 쪽을 잊는다.
 *
 * 그 안에 한 곳도 없으면 FAR 까지 넓혀서라도 보여준다. 한적한 데서는 가장
 * 가까운 가게가 100m 밖일 수 있는데, 빈 시트를 내미는 것보다 거리를 적어
 * 보여주고 사용자가 판단하게 하는 편이 낫다.
 */
const RADIUS_M = 80
const RADIUS_FAR_M = 200

/**
 * 후보를 훑는 반경의 최대치.
 *
 * 시장·푸드코트처럼 넓은 곳은 사진이 수백 m 에 걸쳐 있어서 200m 로는 끝쪽
 * 가게가 안 잡힌다. 그래서 사진이 퍼진 만큼 넓혀 주되 여기서 멈춘다 —
 * 더 넓히면 옆 동네 가게가 후보에 섞여 고르기가 더 어려워진다.
 */
const RADIUS_MAX_M = 500

/**
 * 시트에 보여줄 가게 수.
 *
 * 더 많이 깔아 둔다고 맞을 확률이 오르지 않는다. 고르는 쪽이 훑어야 할 줄만
 * 늘어난다. 여기 없으면 '직접 찾기'가 더 빠르다.
 *
 * 2026-10-17 에 3 으로 줄였다가 **같은 날 되돌렸다.** 실제 사진으로 재 보니
 * 뒤쪽이 '먼 가게'가 아니었다 — 1위 35m, 4위 57m 로 **22m 차이**였고 정답은
 * 4·5위였다. 실내 사진의 GPS 오차가 그보다 커서 앞 네댓 개의 순서는 사실상
 * 우연이다. 한 건물에 가게가 넷 들어 있으면 거리로는 끝내 못 가린다.
 * 줄이려면 순서를 믿을 수 있게 된 다음이다.
 */
const MAX_CANDIDATES = 5

/*
 * 사진 좌표 권한(ACCESS_MEDIA_LOCATION)을 묻던 자리였다. 2026-10-17 에 걷어냈다.
 *
 * 왜 — **켤 수 없는 권한이기 때문이다.** 안드로이드는 이 권한을 단독으로
 * 요청할 수 없다(창이 뜨지 않고 3ms 만에 거부된다, 2026-10-13 실측).
 * 창이 뜨는 길은 READ_MEDIA_IMAGES 와 함께 묻는 것뿐인데 그쪽은 Play 의
 * 사진 권한 정책 대상이라 우리가 못 쓴다. 그래서 10-16 에 앱 매니페스트에서
 * 아예 제거했다(AndroidManifest.xml, tools:node="remove").
 *
 * 권한은 없앴는데 안내만 남으니 **끌 수 없는 잔소리**가 됐다 — 띠가 늘 떠
 * 있고, 눌러도 아무 일이 없고, 설정에 가도 그런 항목이 없다. 그래서 띠와
 * 자동 요청을 함께 걷어냈다.
 *
 * 앱 쪽 통로(getPhotoLocationPermission 등)는 그대로 있다. 부르지 않을 뿐이다 —
 * 앱은 심사를 거쳐야 바뀌므로 웹에서 먼저 멈춘다. 되살릴 일은 안드로이드가
 * 이 권한을 단독으로 물을 수 있게 되는 날뿐이고, 그때는 앱부터 고쳐야 한다.
 */

interface SharedPhoto {
  id: string
  data: string
  takenAt?: string
  takenAtFromExif?: boolean
  hasLocation?: boolean
  lat?: number
  lng?: number
}

interface Candidate {
  /** 우리 DB 에 이미 있는 가게면 그 id. 아직 없으면 비어 있고 kakao 로 등록한다. */
  id?: string
  label: string
  /** '서울 성동구' — 같은 이름이 여럿일 때 어느 쪽인지 가른다 */
  region?: string
  distanceM: number
  isBookmarked?: boolean
  hasReview?: boolean
  /** 아직 우리 DB 에 없는 가게의 카카오 원본 (고르는 순간 등록한다) */
  kakao?: KakaoPlace
}


/**
 * 이름 맞대보기용 꼴.
 *
 * 우리 DB 의 가게는 카카오에서 등록한 것이라 이름이 같다. 다만 띄어쓰기가
 * 들쭉날쭉해서("스타벅스 강남점" / "스타벅스강남점") 공백을 지우고 견준다.
 */
const normName = (s: string) => s.replace(/\s+/g, '').toLowerCase()

/**
 * 카카오에 **두 번 올라간 한 가게**인가.
 *
 * '어경재키친'과 '어경재파스타'는 같은 주소(천호대로 1162)에 3m 떨어져 등록된
 * 같은 집이다. 둘 다 후보에 올리면 **정답 하나가 두 칸을 먹는다** — 다섯 칸이
 * 다섯 가게가 아니게 된다.
 *
 * 판단 기준은 **같은 도로명 주소 + 이름 앞 세 글자**다. 주소만으로는 안 된다 —
 * 그 건물에는 춘천양반닭갈비·수복강녕 같은 **다른** 집도 함께 있다. 앞 세 글자는
 * 상호를 가르는 몸통이라, 같은 건물에서 그것까지 겹치면 같은 집으로 본다.
 * (한 글자·두 글자로 잡으면 '본죽'과 '본가'가 한 줄이 된다.)
 */
const SAME_STORE_STEM = 3

const sameStore = (
  a: { addr?: string; label: string },
  b: { addr?: string; label: string },
) => {
  if (!a.addr || !b.addr || a.addr !== b.addr) return false
  const x = normName(a.label)
  const y = normName(b.label)
  if (x === y || x.startsWith(y) || y.startsWith(x)) return true
  return (
    x.length >= SAME_STORE_STEM &&
    y.length >= SAME_STORE_STEM &&
    x.slice(0, SAME_STORE_STEM) === y.slice(0, SAME_STORE_STEM)
  )
}

/** 두 좌표 사이 거리(m). */
const distanceM = (
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
) => {
  const R = 6371000
  const p1 = (a.lat * Math.PI) / 180
  const p2 = (b.lat * Math.PI) / 180
  const dp = p2 - p1
  const dl = ((b.lng - a.lng) * Math.PI) / 180
  const h =
    Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

/**
 * 카카오 주소에서 앞 두 조각만. '서울 성동구 성수동2가 302-9' → '서울 성동구'
 *
 * 왜 두 조각인가 — 후보 목록은 **같은 이름의 다른 가게를 가르는 자리**다.
 * 동·번지까지 적으면 줄이 길어져 상호가 밀리는데, 구 이름이면 "아, 성수"
 * 정도로 충분히 갈린다. 세종처럼 조각이 하나뿐인 주소도 있어 있는 만큼만 쓴다.
 */
const regionOf = (address?: string): string | undefined => {
  const parts = (address ?? '').trim().split(/\s+/).filter(Boolean)
  return parts.length === 0 ? undefined : parts.slice(0, 2).join(' ')
}

/**
 * 사진 한 칸의 모양.
 *
 * 기록 화면(StoreReview)의 사진 타일이 124×155 — 4:5 세로에 모서리 16px 이다.
 * 같은 사진을 두 화면에서 보는데 한쪽만 정사각이면 **다른 사진처럼 보인다.**
 * 특히 세로 사진이 정사각으로 잘리면 접시가 반쯤 날아간다.
 */
const PHOTO_TILE =
  'relative aspect-[4/5] rounded-16px overflow-hidden bg-472410-5%'

interface PhotoTileProps {
  id: string
  src: string
  on: boolean
  /** 고른 차례. 0 이면 번호를 안 붙인다. */
  order: number
  pressed: boolean
  onDown: (id: string, e: React.PointerEvent) => void
  onMove: (e: React.PointerEvent) => void
  onUp: (id: string, e: React.PointerEvent) => void
  onCancel: () => void
  onActivate: (id: string, e: React.MouseEvent) => void
}

/**
 * 사진 한 칸.
 *
 * **왜 따로 떼어 memo 로 감쌌나** — 한 장 고를 때마다 칸 전체가 다시 그려졌다.
 * 사진은 `data:image/jpeg;base64,…` 로 통째로 들고 있고(앱이 그렇게 건네준다)
 * 스크롤하면 100장도 쌓인다. 거기에 탭 한 번이 상태를 세 번 바꾼다 —
 * 누를 때, 뗄 때, 고른 것이 바뀔 때. 100칸 × 3번이 매 탭마다 돌아
 * **아이폰 웹뷰에서 탭이 굼떴다.** 이제 값이 실제로 바뀐 칸만 다시 그린다.
 *
 * 그래서 넘기는 값은 전부 원시값이고, 함수는 바깥에서 useCallback 으로
 * 고정해 보낸다. 하나라도 매번 새로 만들면 memo 가 통째로 무의미해진다.
 */
const PhotoTile = React.memo<PhotoTileProps>(function PhotoTile({
  id,
  src,
  on,
  order,
  pressed,
  onDown,
  onMove,
  onUp,
  onCancel,
  onActivate,
}) {
  return (
    <button
      type="button"
      /*
        손을 뗄 때 바로 처리한다. click 을 기다리면 아이폰 웹뷰가 '탭인지
        스크롤인지' 가늠하는 시간만큼 늦는다. onClick 은 키보드(Enter/Space)
        처럼 포인터 없이 오는 경우에만 남겨 둔다.

        길게 누르면 지우기로 들어간다. touchAction:'pan-y' 가 있어야 누르는
        중에도 세로 스크롤이 살아 있고, onContextMenu 를 막아야 안드로이드에서
        기본 메뉴가 안 뜬다. iOS 저장/복사 메뉴는 index.css 가 전역으로 꺼 뒀다.
      */
      onPointerDown={(e) => onDown(id, e)}
      onPointerMove={onMove}
      onPointerUp={(e) => onUp(id, e)}
      onPointerCancel={onCancel}
      /*
        아이폰이 연속 탭을 '두 번 쳐서 확대'로 묶어 삼키는 것을 막는다.

        public/index.html 의 viewport 에 maximum-scale 이 없어 페이지가 확대
        가능하다. 그래서 아이폰은 탭마다 두 번째 탭을 기다려 보는데, 그
        판정 창(≈300ms)보다 빠르게 치면 **두 번째 탭이 제스처 후보로 묶여
        pointerup 대신 pointercancel 로 끝난다** — 빠를수록 많이 먹힌다.
        touch-action:'pan-y' 로 막혔어야 하지만 아이폰은 이걸 늘 지키지 않는다.

        touchend 의 기본 동작을 막으면 그 칸에서는 두 번 치기 묶음이 아예
        생기지 않는다. 확대를 앱 전체에서 끄지 않고 이 칸만 빼내는 방법이다.
        손짓 처리는 이미 pointerup 에서 끝냈으니 여기서 잃는 것은 없다 —
        뒤따라올 click 도 같이 사라진다(키보드 click 은 touchend 가 없어 남는다).
      */
      onTouchEnd={(e) => e.preventDefault()}
      onClick={(e) => onActivate(id, e)}
      onContextMenu={(e) => e.preventDefault()}
      style={{ touchAction: 'pan-y' }}
      aria-pressed={on}
      /*
        누르는 즉시 살짝 줄어든다. 길게 누르기는 600ms 라 그동안 아무 표시가
        없으면 화면이 죽은 것처럼 보인다. 진동은 갤럭시만 울려서(아이폰
        웹뷰엔 navigator.vibrate 가 없다) **눈에 보이는 신호**가 양쪽에 다 필요하다.
      */
      className={
        PHOTO_TILE +
        ' transition-transform duration-100' +
        (pressed ? ' scale-[0.96]' : '')
      }
    >
      {/*
        안 고른 쪽을 흐리게 한다(기록 화면의 사진 추천 타일과 같은 50%).
        고른 쪽에는 몇 번째로 골랐는지 번호를 붙인다 — 여러 장을 한 기록으로
        묶을 때 순서가 그대로 사진 차례가 된다.
      */}
      <img
        src={src}
        alt=""
        className={
          'w-full h-full object-cover transition-opacity duration-150' +
          (on ? '' : ' opacity-50')
        }
      />
      {on && (
        <span className="absolute top-6px right-6px w-22px h-22px rounded-full bg-4B3621-80% text-white text-12px font-700 flex items-center justify-center tabular-nums">
          {order}
        </span>
      )}
    </button>
  )
})

/**
 * 길게 누른 것으로 치는 시간.
 *
 * 앱의 다른 길게 누르기와 같은 값이다(UserListItem 600ms). 댓글 삭제는
 * 1000ms 인데, 거기는 누르는 대상이 글이라 스크롤과 헷갈리기 쉬워 더 길다.
 * 사진 칸은 목표가 또렷해서 600 이면 충분하다.
 */
const LONG_PRESS_MS = 600


/** StoreReview 의 toVisitDate 와 같은 형식: 'YYYY-MM-DD HH:mm' */
const toVisitDate = (iso?: string): string | null => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const p2 = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())} ` +
    `${p2(d.getHours())}:${p2(d.getMinutes())}`
  )
}

const Collect: React.FC = () => {
  const navigate = useNavigate()

  const [photos, setPhotos] = useState<SharedPhoto[] | null>(null)
  const [total, setTotal] = useState(0)
  const [selected, setSelected] = useState<string[]>([])

  /**
   * 지우려고 고른 사진.
   *
   * 기록용 selected 와 **따로 둔다.** 섞으면 실수로 길게 누른 사람이
   * 애써 맞춰 둔 사진 차례를 잃는다. 삭제 모드에서는 번호를 감추기만 하고,
   * 취소하면 그대로 돌아온다.
   */
  const [deleting, setDeleting] = useState(false)
  const [marked, setMarked] = useState<string[]>([])
  /**
   * 이 화면이 받을 것이 없는 이유.
   *
   * 'web'  — 앱이 아니다. 공유라는 길 자체가 없다.
   * 'old'  — 앱은 맞는데 사진을 건네주는 통로가 없는 옛 빌드다(1.0.43 미만).
   *
   * 둘을 나누는 이유는 **할 수 있는 일이 다르기 때문**이다. 옛 앱 사용자에게
   * '앱에서 공유하세요'는 해 봐야 안 되는 일을 시키는 말이다.
   */
  const [unsupported, setUnsupported] = useState<null | 'web' | 'old'>(null)

  const [picking, setPicking] = useState(false)
  const [adding, setAdding] = useState(false)
  const [candidates, setCandidates] = useState<Candidate[] | null>(null)
  const [busy, setBusy] = useState(false)

  // ── 앱 브리지에 붙어 사진을 받아온다 ───────────────────────────────
  useEffect(() => {
    if (!isApp()) {
      setUnsupported('web')
      return
    }

    let cancelled = false
    let timer: number | null = null
    let bound: any = null

    const attach = (b: any) => {
      bound = b

      b.onSharedPhotos = (d: {
        total?: number
        offset?: number
        images?: SharedPhoto[]
      }) => {
        if (cancelled) return
        const got = (d?.images ?? []).filter((p) => p?.id && p?.data)
        setTotal(d?.total ?? got.length)
        /*
         * 첫 쪽이면 갈아끼운다.
         *
         * 덧붙이기만 하면 같은 사진이 여러 번 쌓인다. 이 화면은 한 번만
         * 요청하는 것처럼 보이지만 실제로는 여러 번 들어온다 — 개발 모드의
         * 이중 마운트, 뒤로 왔다 가기, 공유를 다시 받는 경우. 실기기에서
         * offset:0 요청이 네 번 들어와 아홉 장이 서른여섯 장으로 보였다.
         *
         * 이어지는 쪽은 덧붙이되 이미 있는 id 는 거른다.
         */
        setPhotos((prev) => {
          const base = (d?.offset ?? 0) === 0 ? [] : prev ?? []
          const seen = new Set(base.map((p) => p.id))
          return [...base, ...got.filter((p) => !seen.has(p.id))]
        })

        // 다음 쪽이 남았으면 이어서 받는다
        const next = (d?.offset ?? 0) + got.length
        if (got.length > 0 && next < (d?.total ?? 0)) {
          b.getSharedPhotos({ offset: next, limit: PAGE })
        }
      }

      b.onSharedPhotosPicked = (d: {
        saved?: number
        dropped?: number
        failed?: boolean
        redacted?: boolean
      }) => {
        if (cancelled) return
        setAdding(false)
        if (d?.failed) {
          toastUtil.warning('사진을 담지 못했어요')
          return
        }
        if (d?.dropped) {
          toastUtil.warning(`한 번에 ${MAX_PER_SHARE}장만 가져와요`)
        }
      }

      b.getSharedPhotos({ offset: 0, limit: PAGE })
    }

    /**
     * 핸들러는 페이지가 뜬 뒤 하나씩 주입된다. FlutterBridge 객체는 있는데
     * getSharedPhotos 는 아직 없는 순간이 있어서, 한 번 보고 없으면 잠깐 기다린다.
     */
    const started = Date.now()

    /*
     * 옛 빌드는 5초를 다 기다리지 않는다.
     *
     * 주입은 한 묶음으로 일어난다(webview_page.dart onPageFinished). 옛날부터
     * 있던 통로가 보이는데 사진 통로만 없으면 **주입은 이미 끝났고 이 앱엔
     * 없는 것**이다. 그 뒤로 더 기다려 봐야 5초 내내 빈 스피너만 돌고
     * 결론은 같다 — 구버전 사용자가 가장 많이 보는 화면이 그 스피너였다.
     *
     * 다만 주입은 한 줄씩 들어와 둘 사이에 짧은 틈이 있다. 표시를 본 즉시가
     * 아니라 GRACE_MS 를 더 보고 판정해, 그 틈에 걸린 최신 앱을 잘못 내치지
     * 않는다.
     */
    let doneSeen: number | null = null

    const giveUp = (why: 'old') => {
      if (timer !== null) window.clearInterval(timer)
      timer = null
      setUnsupported(why)
    }

    const tryAttach = () => {
      if (cancelled) return
      const b = bridge()
      if (hasSharedPhotos(b)) {
        if (timer !== null) window.clearInterval(timer)
        timer = null
        attach(b)
        return
      }
      const now = Date.now()
      if (injectionDone(b)) {
        if (doneSeen === null) doneSeen = now
        if (now - doneSeen >= GRACE_MS) {
          giveUp('old')
          return
        }
      }
      if (now - started > BRIDGE_WAIT_MS) {
        giveUp('old') // 끝내 안 나타났다 — 이 통로가 없는 구버전 앱
      }
    }

    tryAttach()
    if (!bound) timer = window.setInterval(tryAttach, BRIDGE_POLL_MS)

    return () => {
      cancelled = true
      if (timer !== null) window.clearInterval(timer)
      if (bound) {
        delete bound.onSharedPhotos
        delete bound.onSharedPhotosPicked
      }
    }
  }, [])

  /*
   * 공유로 100장을 넘겨 보냈을 때.
   *
   * 앱이 넘친 장수를 주소에 실어 보낸다(share_intent_handler.collectUrlFor).
   * 이걸 안 읽으면 초과분이 **아무 말 없이** 사라진다 — 사용자는 몇 장이
   * 없어졌는지도 모른다.
   *
   * **사진이 한 장이라도 뜬 뒤에** 보여준다. 화면에 뜨자마자 띄웠더니 공유
   * 직후의 앱 전환·로딩과 겹쳐 2초가 로딩 화면 위에서 다 지나가 버렸다
   * (실측: 토스트는 분명히 떠 있었는데 사용자 눈에는 안 보였다).
   * 같은 이유로 기본 2초 대신 더 오래 둔다.
   *
   * 읽고 나서 주소에서 지운다. 안 지우면 새로고침할 때마다 다시 뜬다.
   */
  /*
   * 보관함 상한에 밀려 지워진 장수(evicted)는 **알리지 않는다.**
   *
   * 보관함도 한 번에 받는 양과 같은 100장이라, 100장을 공유하면 이전 것이
   * 통째로 밀린다 — 즉 쓸 때마다 뜨는 안내가 된다. 매번 뜨는 경고는 읽히지
   * 않고 방해만 된다.
   *
   * 주소에는 그대로 실려 온다(share_intent_handler.collectUrlFor). 나중에
   * 보관함을 더 크게 잡아 '가끔' 밀리게 되면 그때 띄우면 된다.
   */

  const droppedShownRef = useRef(false)
  useEffect(() => {
    if (droppedShownRef.current || photos === null) return
    const dropped = Number(new URLSearchParams(window.location.search).get('dropped'))
    if (!Number.isFinite(dropped) || dropped <= 0) return

    droppedShownRef.current = true
    toastUtil.warning(`한 번에 ${MAX_PER_SHARE}장만 가져와요`, { duration: 5000 })

    const url = new URL(window.location.href)
    url.searchParams.delete('dropped')
    window.history.replaceState(null, '', url.pathname + url.search + url.hash)
  }, [photos])

  /*
   * 누른 한 장만 고른다.
   *
   * 예전에는 같은 끼니로 보이는 사진(90분·100m 안)을 함께 골랐다. 여러 장의
   * 좌표를 중앙값으로 모으면 가게를 더 정확히 잡기 때문이었는데, 한 장을
   * 눌렀을 때 넷이 켜지는 것이 오작동으로 읽혔다. 정확도보다 "누른 대로
   * 된다"가 먼저라고 판단했다(2026-10-19).
   *
   * 그 대신 한 장만 고르면 그 사진의 GPS 오차를 그대로 믿게 된다 —
   * 후보가 엉뚱하게 나오면 사용자가 여러 장을 직접 고르면 나아진다.
   */
  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
      if (prev.length >= MAX_PHOTOS) {
        /*
          '한 번에'라고 쓰면 안내의 '한 번에 100장까지 담을 수 있어요'와
          같은 말로 들려 10 과 100 중 어느 쪽이 맞는지 헷갈린다. 담는 한도와
          기록 한도는 다른 숫자이므로 말도 다르게 쓴다.
        */
        toastUtil.warning(`기록 하나에 ${MAX_PHOTOS}장까지 고를 수 있어요`)
        return prev
      }

      return [...prev, id]
    })
  }, [])

  // ── 길게 눌러 지우기 ──────────────────────────────────────────────

  const pressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** 길게 누름이 터진 직후 따라오는 click 을 한 번 삼키는 표시. */
  const pressFiredRef = useRef(false)
  /** 뒤로가기용으로 쌓아 둔 칸이 있는지. */
  const pushedRef = useRef(false)
  /** 지금 손가락이 눌러 둔 칸. 눌린 티를 내는 데만 쓴다. */
  const [pressing, setPressing] = useState<string | null>(null)
  /** 누르기 시작한 자리. 여기서 얼마나 밀렸는지로 '누름'과 '쓸어넘김'을 가른다. */
  const pressFromRef = useRef<{ x: number; y: number } | null>(null)

  const cancelPress = useCallback(() => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current)
      pressTimerRef.current = null
    }
    pressFromRef.current = null
    setPressing(null)
  }, [])
  useEffect(() => cancelPress, [cancelPress])

  /**
   * 누른 채 손가락이 밀렸는지 본다.
   *
   * 전에는 `onPointerLeave` 로 칸을 벗어나는 순간 취소했다. 사람 목록
   * (UserListItem)은 줄이 화면 폭만 해서 그걸로 충분했는데, 여기 칸은
   * 폭의 1/3 에 사이가 6px 뿐이라 **손가락이 조금만 흔들려도 길게 누르기가
   * 죽었다.** 그래서 칸 경계가 아니라 **움직인 거리**로 가른다. 손가락은
   * 포인터를 붙잡아(setPointerCapture) 칸 밖으로 나가도 계속 따라온다.
   */
  const PRESS_SLOP_PX = 12

  const movePress = useCallback(
    (e: React.PointerEvent) => {
      const from = pressFromRef.current
      if (!from) return
      const dx = e.clientX - from.x
      const dy = e.clientY - from.y
      if (dx * dx + dy * dy > PRESS_SLOP_PX * PRESS_SLOP_PX) cancelPress()
    },
    [cancelPress],
  )

  const enterDelete = useCallback((id: string) => {
    setMarked([id])
    setDeleting(true)
    // 갤럭시만 울린다. 아이폰 웹뷰에는 이 기능이 아예 없어 조용히 지나간다.
    try {
      if (navigator.vibrate) navigator.vibrate(30)
    } catch {
      /* 진동이 안 돼도 하던 일은 그대로 */
    }
  }, [])

  const startPress = useCallback((id: string, e: React.PointerEvent) => {
    pressFiredRef.current = false
    cancelPress() // 앞선 누름의 흔적부터 치우고 시작한다

    try {
      // 칸 밖으로 밀려도 move·up 이 이 칸으로 계속 온다
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      /* 못 붙잡아도 길게 누르기 자체는 돈다 */
    }
    pressFromRef.current = { x: e.clientX, y: e.clientY }
    setPressing(id)

    if (deleting) return // 이미 모드면 길게 눌러도 할 일이 없다
    pressTimerRef.current = setTimeout(() => {
      pressTimerRef.current = null
      pressFiredRef.current = true
      enterDelete(id)
    }, LONG_PRESS_MS)
  }, [cancelPress, deleting, enterDelete])

  const toggleMark = useCallback(
    (id: string) =>
      setMarked((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      ),
    [],
  )

  const applyTap = useCallback(
    (id: string) => {
      if (deleting) toggleMark(id)
      else toggle(id)
    },
    [deleting, toggleMark, toggle],
  )

  /**
   * 손을 뗄 때 바로 처리한다.
   *
   * 전에는 `click` 을 기다렸다. 아이폰 웹뷰는 손이 떨어진 뒤에도 '탭인가
   * 스크롤인가'를 한 번 더 가늠하고 click 을 보내서, 그만큼 늦게 반응했다.
   * 포인터는 이미 끝까지 따라가고 있으니 여기서 끝내면 된다.
   *
   * 밀려서(`movePress`) 혹은 스크롤로(`onPointerCancel`) 취소된 손짓은
   * `pressFromRef` 가 비어 있어 여기를 그냥 지나간다 — 쓸어넘기다가 사진이
   * 골라지지 않는다.
   */
  const endPress = useCallback(
    (id: string) => {
      const wasTap = pressFromRef.current !== null && !pressFiredRef.current
      cancelPress()
      pressFiredRef.current = false
      if (wasTap) applyTap(id)
    },
    [cancelPress, applyTap],
  )

  /**
   * 키보드(Enter·Space)로 온 활성화만 여기서 처리한다.
   *
   * 손가락·마우스로 온 click 은 `endPress` 가 이미 처리했으니 버린다.
   * 전에는 "포인터에서 처리했다"는 깃발을 하나 세워 두고 삼켰는데, 그 방식은
   * **빠른 연속 탭에서 무너졌다.** 아이폰 웹뷰는 손을 뗀 뒤에도 click 을
   * 한참 뒤에 보내는데, 그 사이 다음 탭이 시작되면 깃발이 지워져서
   * 뒤늦게 도착한 click 이 직전에 고른 사진을 되돌려 버렸다(켜자마자 꺼짐).
   *
   * 그래서 깃발을 버리고 **그 click 하나만 보고** 가른다. 이벤트마다
   * 따로 판단하니 몇 개가 밀려 들어와도 순서에 영향받지 않는다.
   *   - 키보드가 만든 click: `detail === 0` 이고 `pointerType` 이 비어 있다.
   *   - 손가락·마우스가 만든 click: `detail >= 1`, `pointerType` 은 'touch'·'mouse'.
   */
  const tapTile = useCallback(
    (id: string, e: React.MouseEvent) => {
      const native = e.nativeEvent as PointerEvent
      const fromPointer =
        e.detail > 0 || ('pointerType' in native && !!native.pointerType)
      if (fromPointer) return
      applyTap(id)
    },
    [applyTap],
  )

  /*
   * 삭제 모드는 화면이 아니라 상태다. 그냥 두면 **안드로이드 하드웨어
   * 뒤로가기가 모드를 닫지 않고 화면을 떠난다.** 모드를 열 때 뒤로가기
   * 한 칸을 쌓아 두고, 그 칸이 뽑힐 때 모드를 닫는다.
   */
  useEffect(() => {
    if (!deleting) return
    window.history.pushState({ collectDelete: true }, '')
    pushedRef.current = true
    const onPop = () => {
      pushedRef.current = false
      setDeleting(false)
      setMarked([])
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [deleting])

  /** 모드를 닫는 유일한 통로. 쌓아 둔 칸을 되돌려 줘야 뒤로가기가 안 꼬인다. */
  const closeDelete = () => {
    if (pushedRef.current) {
      window.history.back() // 위 popstate 가 상태를 정리한다
      return
    }
    setDeleting(false)
    setMarked([])
  }

  const removeMarked = async () => {
    if (marked.length === 0) return
    const ids = marked
    const ok = await dialogUtil.confirm({
      /*
        둘째 줄이 이 창의 전부다. 없으면 "내 사진이 지워지나?" 때문에
        아무도 못 누른다 — 지우는 건 앱이 들고 있던 사본뿐이다.
      */
      message: `사진 ${ids.length}장을 지울까요?\n갤러리 원본은 지워지지 않아요`,
      confirmText: '지우기',
      cancelText: '취소',
    })
    if (!ok) return

    /*
     * 앱에 지우라고만 하고 **답은 기다리지 않는다** — 이 통로는 돌려주는 값이
     * 없다(webview_shared_photos_handler 의 removeSharedPhotos). 그래서 화면에서
     * 먼저 지우고, 혹시 앱 쪽이 실패하면 다음에 들어올 때 되살아난다.
     * 잃는 쪽이 아니라 남는 쪽으로 틀리므로 이 방향이 안전하다.
     */
    try {
      const b = bridge()
      if (typeof b?.removeSharedPhotos === 'function') {
        b.removeSharedPhotos({ ids })
      }
    } catch (e) {
      console.warn('담아둔 사진 지우기 실패', e)
    }

    setPhotos((prev) => (prev ?? []).filter((p) => !ids.includes(p.id)))
    setSelected((prev) => prev.filter((x) => !ids.includes(x)))
    setTotal((t) => Math.max(0, t - ids.length))
    toastUtil.success(`${ids.length}장을 지웠어요`)
    closeDelete()
  }

  const chosen = (photos ?? []).filter((p) => selected.includes(p.id))

  /** 고른 사진들의 가운데 좌표. 좌표가 하나도 없으면 null. */
  /**
   * 고른 사진들이 '한 곳'인지 보고, 그 한 곳의 중심을 잡는다.
   *
   * 왜 평균·반경이 아니라 덩어리(클러스터)인가
   * -----------------------------------------
   * 전에는 평균을 중심으로 삼고 '가장 먼 사진이 200m 넘으면 다른 곳'으로 봤다.
   * 두 가지가 동시에 틀렸다.
   *
   *   ① 튄 한 장에 통째로 무너진다 — 평균은 이상치 쪽으로 1/N 만 끌려가는데
   *      흩어짐은 거의 그 거리 전체가 된다. 99장이 한 자리에 모여 있어도
   *      250m 밖 한 장이면 경고가 뜬다(실측: 사진이 많을수록 더 잘 걸린다).
   *      실내에서 와이파이로 위치를 잡다 수백 m 튀는 일이 흔하다.
   *   ② 넓은 가게를 못 받는다 — 시장·푸드코트·백화점은 한 곳인데도 수백 m 다.
   *      거리만 재면 '다른 곳'이 된다.
   *
   * 그래서 거리 대신 **이어져 있는지**를 본다. 서로 LINK_M 안에 있는 사진끼리
   * 사슬처럼 묶어 덩어리를 만든다. 400m 짜리 시장은 사진이 군데군데 있으면
   * 하나로 이어지고, 성수와 성북구처럼 멀리 떨어진 두 곳은 끊어진다.
   *
   * 덩어리가 여럿이면 **가장 큰 것**으로 간다. 나머지는 튄 좌표이거나,
   * 정말 다른 곳이더라도 사용자가 고를 목록은 있는 편이 낫다.
   *
   * 왜 경고하지 않나
   * ---------------
   * 전에는 '서로 다른 곳에서 찍혔어요. 한 곳씩 나눠서 골라주세요' 를 띄우고
   * 후보를 비웠다. 그건 **평균을 쓰던 시절의 사정**이었다 — 성수와 성북구를
   * 섞으면 가운데인 왕십리가 나왔고, 그 가짜 자리로 가게를 찾느니 아무것도
   * 안 내미는 게 나았다.
   *
   * 덩어리로 바꾸면서 가짜 중심이 안 나온다. 가장 큰 덩어리의 중앙값은 언제나
   * **사진이 실제로 찍힌 자리**다. 그러면 보여줄 수 있는 걸 보여주지 않고
   * 사용자한테 다시 고르라고 떠넘길 이유가 없다. 틀렸으면 직접 찾기로 가면
   * 되고, 그 길은 어차피 항상 열려 있다.
   */
  const LINK_M = 300

  type Pt = { lat: number; lng: number }

  const located = (list: SharedPhoto[]): Pt[] =>
    list
      .filter((p) => typeof p.lat === 'number' && typeof p.lng === 'number')
      .map((p) => ({ lat: p.lat as number, lng: p.lng as number }))

  /** 서로 LINK_M 안에 있는 점들을 사슬로 이어 덩어리를 만든다. 큰 것부터. */
  const clustersOf = (pts: Pt[]): Pt[][] => {
    const seen = new Array(pts.length).fill(false)
    const out: Pt[][] = []

    for (let i = 0; i < pts.length; i++) {
      if (seen[i]) continue
      const group: number[] = [i]
      seen[i] = true
      // 너비 우선으로 이어 붙인다. 100장이면 비교가 1만 번이라 가볍다.
      for (let head = 0; head < group.length; head++) {
        for (let j = 0; j < pts.length; j++) {
          if (seen[j]) continue
          if (distanceM(pts[group[head]], pts[j]) <= LINK_M) {
            seen[j] = true
            group.push(j)
          }
        }
      }
      out.push(group.map((k) => pts[k]))
    }

    return out.sort((a, b) => b.length - a.length)
  }

  /** 중앙값. 평균과 달리 튄 값에 끌려가지 않는다. */
  const medianOf = (pts: Pt[]): Pt => {
    const mid = (xs: number[]) => {
      const v = [...xs].sort((a, b) => a - b)
      const i = Math.floor(v.length / 2)
      return v.length % 2 ? v[i] : (v[i - 1] + v[i]) / 2
    }
    return { lat: mid(pts.map((p) => p.lat)), lng: mid(pts.map((p) => p.lng)) }
  }

  /**
   * @returns null 좌표 있는 사진이 하나도 없음 — 추천할 근거가 없다
   *          그 외  가장 큰 덩어리의 중심과, 그 덩어리를 덮을 검색 반경
   */
  const placeOf = (
    list: SharedPhoto[],
  ): null | { center: Pt; radiusM: number } => {
    const pts = located(list)
    if (pts.length === 0) return null

    const main = clustersOf(pts)[0]
    const center = medianOf(main)
    // 넓은 가게면 그만큼 넓게 훑는다. 끝없이 넓히지는 않는다 —
    // 반경이 커질수록 옆 동네 가게까지 후보에 섞인다.
    const reach = Math.max(...main.map((p) => distanceM(center, p)))
    const radiusM = Math.min(
      RADIUS_MAX_M,
      Math.max(RADIUS_FAR_M, Math.round(reach) + RADIUS_M),
    )

    return { center, radiusM }
  }

  // ── 가게 후보 찾기 ────────────────────────────────────────────────
  const openPicker = async () => {
    setPicking(true)
    setCandidates(null)

    const place = placeOf(chosen)

    if (place === null) {
      // 좌표가 없는 사진(스크린샷·받은 사진)뿐이면 추천할 근거가 없다.
      // 빈 손으로 목록을 꾸미는 대신 검색으로 보낸다.
      setCandidates([])
      return
    }

    const { center, radiusM } = place

    try {
      /*
       * 후보는 **카카오의 실제 가게 목록**에서 뽑는다.
       *
       * 처음엔 우리 DB 의 마커(mapApi.getMarkers)만 썼는데, 그것은 "누군가
       * TASTEE 에 이미 등록한 가게"일 뿐이라 **실제로 그 자리에 있는 가게의
       * 일부**다. 카페에서 찍은 사진인데 길 건너 중국집만 뜨는 일이 그래서
       * 생긴다 — 카페를 거른 게 아니라 그 카페가 우리 DB 에 아직 없는 것이다.
       * (ALL 필터는 카테고리를 전혀 거르지 않는다. 우리 places 는 restaurant·
       *  cafe·bar 를 다 담는다.)
       *
       * 카카오 좌표 검색은 음식점(FD6)과 카페(CE7)를 둘 다 받아 거리순으로
       * 돌려준다. 주점은 FD6 밑이라 같이 온다. 지도를 빈 곳 클릭했을 때 쓰는
       * 바로 그 통로다.
       *
       * 우리 DB 마커는 **버리지 않고 곁들인다.** 담아둔 곳·가 본 곳 표시를
       * 붙여 순서를 조금 당기는 데만 쓴다.
       */
      // 넓은 가게면 radiusM 이 그만큼 커진다 (placeOf 참고)
      const dLat = radiusM / 111320
      const dLng = radiusM / (111320 * Math.cos((center.lat * Math.PI) / 180))

      const [found, mine] = await Promise.all([
        kakaoApi.searchByCoordinate(center.lat, center.lng, radiusM),
        mapApi
          .getMarkers({
            bounds: {
              north: center.lat + dLat,
              south: center.lat - dLat,
              east: center.lng + dLng,
              west: center.lng - dLng,
            },
            filter: MapFilterType.ALL,
          })
          .catch(() => ({ markers: [] as MapMarker[] })),
      ])

      // 이름으로 맞춘다. 우리 가게는 카카오에서 등록한 것이라 이름이 같다.
      const mineByName = new Map<string, MapMarker>()
      for (const m of mine.markers ?? []) mineByName.set(normName(m.label), m)

      /*
       * 가까운 순이 먼저다.
       *
       * 처음엔 "담아둔 곳 → 가 본 곳" 순으로만 세웠는데, 그러면 **바로 그 자리
       * 가게가 목록에 아예 안 들어올 수 있다**. 실제로 "답이 없는 경우가 잦다"로
       * 드러났다.
       *
       * 담아둔 곳·가 본 곳은 순서를 뒤집지 않고 **거리를 조금 깎아주는** 정도로만
       * 쓴다. GPS 오차가 수십 m 라 그 안에서는 관심을 보인 곳이 맞을 때가 많지만,
       * 100m 밖의 담아둔 곳이 코앞의 가게를 제치면 안 된다.
       */
      const BOOKMARK_BONUS_M = 40
      const REVIEW_BONUS_M = 20

      const ranked = found
        .map((k) => {
          // 카카오가 중심에서의 거리를 함께 준다. 없으면 직접 잰다.
          const d = Number(k.distance)
          const distance = Number.isFinite(d)
            ? d
            : distanceM(center, { lat: Number(k.y), lng: Number(k.x) })
          const m = mineByName.get(normName(k.place_name))
          return {
            id: m?.id,
            label: k.place_name,
            region: regionOf(k.road_address_name || k.address_name),
            /** 같은 가게인지 가리는 데만 쓴다 — 화면에는 region 만 보인다 */
            addr: k.road_address_name || k.address_name,
            distanceM: distance,
            isBookmarked: m?.isBookmarked,
            hasReview: m?.hasReview,
            kakao: m ? undefined : k,
            rank:
              distance -
              (m?.isBookmarked ? BOOKMARK_BONUS_M : 0) -
              (m?.hasReview ? REVIEW_BONUS_M : 0),
          }
        })
        .sort((a, b) => a.rank - b.rank)

      // 코앞(80m)에 있으면 그것만. 없으면 이 덩어리를 덮는 반경까지 넓혀
      // 거리를 적어 보여주고 사용자가 고르게 한다. 넓은 가게는 radiusM 이
      // 200m 보다 크다 — 여기서 200m 로 다시 자르면 끝쪽 가게가 사라진다.
      const near = ranked.filter((m) => m.distanceM <= RADIUS_M)
      const use = near.length > 0
        ? near
        : ranked.filter((m) => m.distanceM <= radiusM)

      /*
       * 한 가게가 두 줄을 먹지 않게 묶는다.
       *
       * 가까운 순으로 훑으며, 앞에 이미 같은 집이 있으면 버린다. 다만 우리 DB 에
       * 있는 쪽을 만나면 그 자리를 **갈아끼운다** — 기록이 새 가게를 만들지 않고
       * 이미 있는 가게에 붙어야 해서다(안 그러면 같은 집이 둘로 쌓인다).
       */
      const merged: typeof use = []
      for (const r of use) {
        const twin = merged.findIndex((d) => sameStore(d, r))
        if (twin === -1) {
          merged.push(r)
        } else if (merged[twin].id === undefined && r.id !== undefined) {
          merged[twin] = r
        }
      }

      setCandidates(merged.slice(0, MAX_CANDIDATES))
    } catch {
      setCandidates([])
    }
  }

  /** 앱에서 원본 한 장을 받아온다. 좌표는 앱이 떼고 준다. */
  const fetchOriginal = useCallback(
    (id: string) =>
      new Promise<{ name: string; data: string } | null>((resolve) => {
        const b = bridge()
        if (!b || typeof b.getSharedPhotoOriginal !== 'function') {
          resolve(null)
          return
        }
        const done = window.setTimeout(() => resolve(null), 15000)
        b.onSharedPhotoOriginal = (d: { id?: string; name?: string; data?: string }) => {
          if (d?.id !== id) return
          window.clearTimeout(done)
          resolve(d?.data ? { name: d.name || id, data: d.data } : null)
        }
        b.getSharedPhotoOriginal({ id })
      }),
    [],
  )

  // ── 고른 사진 + 고른 가게 → 기존 기록 화면으로 ──────────────────────
  const startRecord = async (c: Candidate) => {
    if (busy) return
    setBusy(true)
    try {
      /*
       * 우리 DB 에 아직 없는 가게면 고르는 이 순간 등록한다. 지도에서 빈 곳을
       * 눌러 가게를 발견했을 때와 같은 통로다. 이미 있는 가게를 다시 보내도
       * 기존 id 를 돌려주므로(created:false) 중복으로 생기지 않는다.
       */
      let placeId = c.id
      if (!placeId && c.kakao) {
        const reg = await kakaoApi.registerKakaoPlace(c.kakao)
        if (reg?.place_id != null) placeId = String(reg.place_id)
      }
      if (!placeId) {
        toastUtil.warning('가게를 불러오지 못했어요')
        return
      }

      const items = await buildPhotoItems()
      if (items === null) return

      const rs = useReviewStore.getState()
      rs.resetReviewData()
      rs.setStoreId(placeId) // 넘어간 화면이 상태를 지우지 않도록 먼저 심는다
      stagePhotos(items)

      navigate(`/store-review/${placeId}`)
    } catch {
      toastUtil.warning('사진을 가져오지 못했어요')
    } finally {
      setBusy(false)
    }
  }

  /** 고른 사진을 기록 화면이 쓸 모양으로 만든다. 하나도 못 만들면 null */
  const buildPhotoItems = async (): Promise<PhotoItem[] | null> => {
    const items: PhotoItem[] = []
    for (const p of chosen) {
      const got = await fetchOriginal(p.id)
      // 원본을 못 받으면 미리보기라도 쓴다. 한 장 때문에 전체를 막지 않는다.
      const data = got?.data ?? p.data
      items.push({
        id: p.id,
        file: dataURLtoFile(data, got?.name ?? `${p.id}.jpg`),
        preview: data,
        description: '',
      })
    }
    if (items.length === 0) {
      toastUtil.warning('사진을 가져오지 못했어요')
      return null
    }

    return items
  }

  /** 사진과 찍은 날짜를 기록 상태에 심는다 */
  const stagePhotos = (items: PhotoItem[]) => {
    const rs = useReviewStore.getState()
    rs.setPhotos(items)
    const visitDate = toVisitDate(chosen.find((p) => p.takenAtFromExif)?.takenAt)
    if (visitDate) rs.setVisitDateFromPhoto(visitDate)
  }

  /**
   * '직접 찾기' — 사진을 들고 검색으로 간다.
   *
   * 예전에는 navigate('/search') 만 했다. 그러면 검색에서 가게를 고르는 순간
   * StoreReview 가 "가게가 바뀌었다"고 보고 전역 상태를 비워(StoreReview.tsx의
   * storeId 비교) 사진이 사라졌다. 후보에서 고르는 길은 가게 id를 미리 심어
   * 그 비교를 피하는데, 여기서는 아직 어느 가게인지 모른다.
   *
   * 그래서 사진만 심어 두고 가게 자리는 비워 둔다. StoreReview 는 비어 있던
   * 자리에는 손대지 않으므로(그쪽 주석 참고) 사진이 그대로 따라붙는다.
   */
  const startSearch = async () => {
    if (busy) return
    setBusy(true)
    try {
      const items = await buildPhotoItems()
      if (items === null) return

      const rs = useReviewStore.getState()
      rs.resetReviewData()
      stagePhotos(items)

      navigate('/search')
    } catch {
      toastUtil.warning('사진을 가져오지 못했어요')
    } finally {
      setBusy(false)
    }
  }

  /**
   * 갤러리에서 골라 담는다.
   *
   * 공유 시트를 거치지 않는 길이다. 공유는 앱을 나갔다 들어와야 해서 번거롭고,
   * 무엇보다 **권한을 켜기 전에 담긴 사진은 좌표가 이미 지워져 있다.** 그 사진을
   * 여기서 다시 담으면 같은 사진으로 알아보고 최신 것으로 바뀌면서 좌표가 붙는다.
   */
  const addPhotos = (mode?: 'picker' | 'documents') => {
    const b = bridge()
    if (!b || typeof b.pickSharedPhotos !== 'function') {
      toastUtil.warning('앱을 최신 버전으로 업데이트해 주세요')
      return
    }
    setAdding(true)
    b.pickSharedPhotos({ mode: mode ?? 'picker' })
  }

  /*
   * 보기 좋은 사진 선택기를 기본으로 쓴다. 그게 좌표를 가리는 기기로 드러나면
   * 그때만 폴더에서 고르는 길을 함께 내민다 — 투박하지만 좌표가 반드시 남는다.
   * 미리 둘을 다 보여주면 아무 문제 없는 사람에게도 고민거리를 하나 주는 셈이다.
   */
  /*
   * 앱에서 쓰는 공용 버튼(SubmitButton)을 그대로 쓴다.
   *
   * 전에는 이 화면만 테두리만 있는 버튼을 따로 만들어 썼다. 다른 화면의
   * 주 버튼은 전부 갈색 채움이라 혼자 달라 보였고, 비활성 색·로딩 표시도
   * 각자 구현이었다. 공용 컴포넌트가 셋 다 들고 있다.
   *
   * loading 을 주면 점 세 개가 같이 뜬다 — '담는 중…' 이라는 글자는
   * 100장을 담는 수십 초 동안 한 글자도 안 움직여 멈춘 것처럼 보였다.
   */
  const addButton = (
    <div className="flex flex-col gap-8px">
      <SubmitButton
        text={adding ? '' : '사진 담기'}
        loading={adding}
        onSubmit={() => addPhotos('picker')}
      />
      {/*
        안내 문구와 '폴더에서 고르기'는 뺐다. 안드로이드에서 담기로 좌표가
        오지 않는다는 사실은 그대로지만(실측), 그 설명은 이 버튼 아래가 아니라
        '사진 가져오는 방법' 페이지가 맡는다. 담으려고 누른 사람에게 누르기 전
        설명을 들이밀면 버튼이 둘로 보여 고민만 는다.
      */}
    </div>
  )

  // ── 그리기 ────────────────────────────────────────────────────────

  if (unsupported) {
    return (
      <Shell>
        <Center>
          {unsupported === 'old'
            ? '앱을 최신 버전으로 업데이트하면 쓸 수 있어요'
            : '앱에서 사진을 공유하면 여기에 모여요'}
        </Center>
      </Shell>
    )
  }

  if (photos === null) {
    return (
      <Shell>
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner size={32} />
        </div>
      </Shell>
    )
  }

  if (photos.length === 0) {
    return (
      <Shell>
        {/* 아래 '갤러리에서 사진 담기' 버튼이 이미 글을 위로 밀어 올린다 */}
        <Center nudge={false}>기록할 사진을 담아보세요</Center>
        <div className="pb-24px pt-8px">{addButton}</div>
      </Shell>
    )
  }

  /*
    아래 큰 버튼은 **모드와 상관없이 같은 갈색**이다. 지우기만 빨강으로 두면
    같은 화면이 둘로 보인다. 무엇을 누르는지는 글자가 말하고, 실수는 확인창이
    막는다.
  */
  const picked = deleting ? marked.length : selected.length
  const bottomOff = picked === 0 || (!deleting && busy)
  const bottomLabel =
    picked > 0
      ? `${picked}장 ${deleting ? '지우기' : '기록하기'}`
      : deleting
        ? '지울 사진 선택하기'
        : '기록할 사진 선택하기'

  return (
    <Shell
      /*
        지우는 중에는 제목이 **'사진 지우기'로 고정**이다. 고른 장수까지
        제목에 넣으면 위아래 두 곳이 같이 바뀌어 눈이 어디를 봐야 할지
        흩어진다. 숫자는 아래 버튼 한 곳에서만 센다.
      */
      title={deleting ? '사진 지우기' : undefined}
      right={
        deleting ? (
          <button
            type="button"
            onClick={closeDelete}
            className="text-style-button1 text-textColorText2 cursor-pointer hover:opacity-70 px-16px h-48px whitespace-nowrap"
          >
            취소
          </button>
        ) : undefined
      }
    >
      {/*
        스크롤 상자를 좌우 여백(20px)까지 넓히고 여백은 안쪽에 둔다.
        휴대폰 스크롤 막대는 스크롤 상자의 오른쪽 끝 안쪽, 내용 위에 그려진다.
        상자가 그리드와 폭이 같으면 막대가 오른쪽 줄 사진 위에 겹친다.
      */}
      <div className="flex-1 min-h-0 overflow-y-auto -mx-20px px-20px">
        <div className="grid grid-cols-3 gap-6px pb-16px">
          {/*
            사진을 더하는 자리를 **그리드 맨 앞 칸**에 둔다.

            전에는 하단에 '사진 담기' 버튼을 따로 뒀는데, 그러면 '기록하기'와
            나란히 갈색 버튼이 둘이 되어 어느 쪽이 주인지 사라졌다. 사진을
            더하는 일은 사진들이 있는 자리에서 하는 편이 자연스럽고, 맨 앞
            칸이라 스크롤 없이 바로 보인다.
          */}
          {/*
            지우는 중에도 **자리는 지킨다.**

            전에는 `{!deleting && …}` 로 아예 뺐다. 그러면 모드가 열리는 순간
            이 칸이 사라지면서 **사진이 전부 한 칸씩 당겨진다** — 길게 누르던
            사진이 손가락 밑에서 옆으로 빠져나가고, 모드를 닫으면 반대로 또
            밀린다. 누른 것과 반응한 것이 어긋나 보이는 가장 큰 이유였다.
            지금 할 일과 반대 방향인 건 맞으니 흐리게 하고 못 누르게만 한다.
          */}
          <button
            type="button"
            onClick={() => addPhotos('picker')}
            disabled={adding || deleting}
            aria-hidden={deleting}
            tabIndex={deleting ? -1 : 0}
            aria-label="사진 담기"
            className={
              PHOTO_TILE +
              ' flex items-center justify-center border border-4B3621-20%' +
              (deleting ? ' opacity-30 pointer-events-none' : '')
            }
          >
            {adding ? (
              <LoadingSpinner size={20} />
            ) : (
              /*
                기록 화면('사진 추가')과 **같은 글리프**를 쓴다.

                photo-plus-icon.png 은 쓰지 않는다 — 그 자산은 5% 배경 박스를
                품고 있어서, 배경·테두리를 그리는 이 버튼 위에 얹으면
                **박스 안에 박스**가 된다(갤럭시 실측). 거기서 + 만 떼어낸
                photo-plus-glyph.png 을 쓴다.
              */
              <img src="/images/photo-plus-glyph.png" alt="" className="w-24px h-24px" />
            )}
          </button>

          {photos.map((p) => {
            /*
              고른 표시는 **두 모드가 똑같다** — 갈색 동그라미에 고른 차례.
              지울 때만 빨간 체크를 쓰면 같은 화면이 둘로 보인다. 무엇을
              하는 중인지는 앱바 제목과 아래 버튼 글자가 말해 준다.
            */
            const n = (deleting ? marked : selected).indexOf(p.id)
            return (
              <PhotoTile
                key={p.id}
                id={p.id}
                src={p.data}
                on={n >= 0}
                order={n + 1}
                pressed={pressing === p.id}
                onDown={startPress}
                onMove={movePress}
                onUp={endPress}
                onCancel={cancelPress}
                onActivate={tapTile}
              />
            )
          })}
        </div>
        {total > photos.length && (
          <p className="pb-16px text-center text-style-captionS text-textColorText3">
            불러오는 중… ({photos.length}/{total})
          </p>
        )}
      </div>

      <div className="pb-24px pt-8px flex flex-col gap-8px">
        <button
          type="button"
          onClick={deleting ? removeMarked : openPicker}
          disabled={bottomOff}
          className={`w-full py-16px rounded-full font-pretendard font-700 text-16px ${
            bottomOff ? 'bg-E8E1D9 text-[#918063]' : 'bg-4B3621-80% text-white'
          }`}
        >
          {bottomLabel}
        </button>
      </div>

      {picking && (
        <PlaceSheet
          candidates={candidates}
          busy={busy}
          onPick={startRecord}
          onSearch={startSearch}
          onClose={() => setPicking(false)}
        />
      )}
    </Shell>
  )
}

/** 어디서 먹었는지 고르는 시트. */
const PlaceSheet: React.FC<{
  candidates: Candidate[] | null
  busy: boolean
  onPick: (c: Candidate) => void
  onSearch: () => void
  onClose: () => void
}> = ({ candidates, busy, onPick, onSearch, onClose }) => (
  <div className="fixed inset-0 z-50 flex flex-col justify-end">
    <button
      type="button"
      aria-label="닫기"
      onClick={onClose}
      className="absolute inset-0 bg-black bg-opacity-40"
    />
    {/*
      앱의 다른 바텀시트와 같은 짜임이다 — 크림 바탕(ivoryKey #F8F4EE),
      위 모서리 30px, 손잡이 40×5, 가운데 제목, 전체폭 분할선.
      흰색이면 이 화면만 혼자 떠 보인다(앱 바탕이 #FAF6EF).
      제목줄은 공용 SheetHeader 를 그대로 쓴다 — 글씨 크기·색·분할선 위치가
      다른 시트들과 1px 도 어긋나지 않게.
    */}
    <div className="relative app-container w-full bg-ivoryKey rounded-t-30px pb-32px max-h-[70vh] overflow-y-auto">
      <div className="w-40px h-5px bg-d9d9d9 rounded-20px mx-auto mt-24px mb-8px" />
      <SheetHeader title="장소 선택" />

      <div className="px-20px pt-28px">
        {candidates === null ? (
          <div className="py-32px flex justify-center">
            <LoadingSpinner size={28} />
          </div>
        ) : candidates.length === 0 ? (
          /*
           * 찾은 게 없으면 **아무 말도 하지 않는다.**
           * 빈 목록은 그 자체로 보이고, 바로 아래 '직접 찾기'가 할 일을
           * 말해 준다. 설명을 한 줄 깔면 읽고 나서도 누를 곳은 같다.
           */
          null
        ) : (
          <div className="flex flex-col gap-12px pb-28px">
            {candidates.map((c) => (
              <button
                key={c.id ?? `kakao:${c.kakao?.id}`}
                type="button"
                disabled={busy}
                onClick={() => onPick(c)}
                className="w-full min-h-48px flex items-center gap-8px px-20px py-12px rounded-12px bg-E8E1D9 text-left"
              >
                {/* 이름은 왼쪽 끝, 지역은 오른쪽 끝 — 긴 이름은 이름만 줄인다 */}
                <span className="flex-1 min-w-0 truncate text-style-body1 text-textColorText1">
                  {c.label}
                </span>
                {/*
                  '저장함'·'가봤음' 표시는 뺐다(2026-10-29, 도연님).
                  후보를 고르는 자리라 필요한 건 "어디 있는 가게인가" 하나다.
                  isBookmarked·hasReview 는 같은 가게를 가려내는 데는 계속
                  쓰이므로 값 자체는 그대로 둔다.
                */}
                {c.region && (
                  <span className="flex-shrink-0 text-style-captionS text-textColorText3">
                    {c.region}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/*
          '직접 찾기'는 앱의 주 버튼 그대로다 — 갈색 채움에 흰 글씨.
          바로 앞 화면의 '기록하기'와 같은 SubmitButton 이라 모양이 어긋날 수 없다.
          후보 줄(베이지)과 색이 달라야 '고르기'와 '찾으러 가기'가 구분된다.
        */}
        <SubmitButton text="직접 찾기" disabled={busy} onSubmit={onSearch} />
      </div>

      {busy && (
        <div className="absolute inset-0 bg-ivoryKey bg-opacity-60 flex items-center justify-center">
          <LoadingSpinner size={32} />
        </div>
      )}
    </div>
  </div>
)

/**
 * 빈 화면 가운데 글.
 *
 * nudge 는 공용 empty-state-center(translateY -5%) 를 켜는 스위치다.
 * 그 5% 는 **아래에 아무것도 없는 화면**을 기준으로 잡힌 값이라, 버튼이
 * 같이 있는 화면에서는 버튼이 밀어 올린 만큼과 겹쳐 두 번 올라간다.
 * 그래서 버튼이 있는 쪽은 끈다.
 */
const Center: React.FC<{ children: React.ReactNode; nudge?: boolean }> = ({
  children,
  nudge = true,
}) => (
  <div
    className={`flex-1 flex items-center justify-center px-16px ${
      nudge ? 'empty-state-center' : ''
    }`}
  >
    <p className="text-style-body1 text-textColorText3 text-center">{children}</p>
  </div>
)

const Shell: React.FC<{
  children: React.ReactNode
  /** 삭제 모드처럼 제목·우측이 바뀌는 경우에만 넘긴다. 없으면 평소 모양. */
  title?: string
  right?: React.ReactNode
}> = ({ children, title, right }) => {
  const navigate = useNavigate()
  return (
  <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
    <AppBar
      title={title ?? '기록할 사진'}
      isBottomBorder
      rightElement={
        right ?? (
        /*
          담는 법으로 가는 길. 기기마다 되는 것이 달라서 설명이 길어지는데,
          그걸 이 화면에 다 적으면 단추보다 안내가 커진다. 한 줄만 남기고
          나머지는 저 페이지로 뺐다.
        */
        <button
          type="button"
          onClick={() => navigate('/collect/guide')}
          /*
            앱바 우측 글자 버튼의 공통 모양 — 랭킹('수정')과 같은 클래스다.

            px-16px·h-48px 가 핵심이다. 여백 없이 두면 글자가 화면 오른쪽
            끝에 붙어 **잘린다**(갤럭시 실측). 슬롯 기본 폭이 48px 이라
            버튼이 왼쪽으로 넘치지만, 넘친 쪽은 가려지지 않아 다 보인다.
          */
          className="text-style-button1 text-textColorText2 cursor-pointer hover:opacity-70 px-16px h-48px whitespace-nowrap"
        >
          방법
        </button>
        )
      }
    />
    <div className="app-container flex-1 min-h-0 flex flex-col px-20px pt-20px overflow-hidden">
      {children}
    </div>
  </div>
  )
}

export default Collect
